import type { FC, ReactNode } from "react";
import React, { useEffect } from "react";

import { useNavigation, useRouteLoaderData } from "@remix-run/react";
import clsx from "clsx";

import { SidebarNav } from "~/components/sidebar-nav";
import { GlobalLoading, useLoader } from "~/context/LoaderContext";
import { useNearTop } from "~/hooks/use-near-top";
import { useScreenSize } from "~/hooks/use-screen-size";
import { useSidebarCollapsed } from "~/hooks/use-sidebar-collapsed";
import { cn } from "~/lib/utils";

import { BillingGlobalBanner } from "~/components/billing/billing-global-banner";
import { BaaNudgeBanner } from "~/features/baa-nudge/components/baa-nudge-banner";
import { DpaAcceptBanner } from "~/features/baa-nudge/components/dpa-accept-banner";
import { ServiceDegradationBanner } from "~/components/service-degradation-banner";
import { OnboardingChecklistContainer } from "~/features/onboarding-checklist/components/onboarding-checklist-container";
import { PromotionBanner } from "./session/promotion-banner";
import { H1 } from "./ui/typography";

export interface LayoutProps {
    children?: ReactNode;
    hideBack?: boolean;
    showLogout?: boolean;
    action?: React.ReactNode;
    backNavOverride?: string;
    loading?: boolean;
    title?: React.ReactNode;
    description?: React.ReactNode;
    searchBar?: React.ReactNode;
    headerBanner?: React.ReactNode;

    /* TODO: these are anti-pattern props and should be reworked */
    displayHeader?: ReactNode;
    offsetTop?: boolean;
    hideLogo?: boolean;
    narrow?: boolean;
    padTop?: boolean;
    fullWidth?: boolean;
    desktopFullHeight?: boolean;
    logoOnly?: boolean;
    actions?: React.ReactNode;
    stickyHeader?: boolean;
    headerImageUrl?: string;
    hideSidebar?: boolean;
    /**
     * Optional data-testid for the layout container (for Playwright tests)
     */
    testId?: string;
    /**
     * AIDEV-NOTE: Right sidebar content - scrolls independently from main content.
     * Rendered as a sticky column on the right side of the main content area.
     * The sidebar has its own scroll area and stays visible while main content scrolls.
     */
    rightSidebar?: React.ReactNode;
    /**
     * Width of the right sidebar. Can be a number (pixels) or string (e.g., "30vw"). Defaults to 400.
     */
    rightSidebarWidth?: number | string;
    /**
     * Optional className override for right sidebar container (default: bg-gray-50)
     */
    rightSidebarClassName?: string;
    /**
     * AIDEV-NOTE: Secondary/inner sidebar content - rendered between main sidebar and content.
     * Used for nested navigation (e.g., admin tools, settings categories).
     * When provided, main sidebar should typically be forced to collapsed state.
     */
    secondarySidebar?: React.ReactNode;
    bleed?: boolean;
    hideHeader?: boolean;
    /**
     * Force the main sidebar to collapsed state regardless of user preference.
     * Typically used together with secondarySidebar.
     */
    forceCollapsed?: boolean;
}

export const DesktopLayout: FC<LayoutProps> = ({
    children,
    backNavOverride,
    title,
    fullWidth = false,
    desktopFullHeight = false,
    actions,
    searchBar,
    loading = false,
    stickyHeader = true,
    headerImageUrl,
    hideSidebar = false,
    headerBanner,
    testId,
    rightSidebar,
    rightSidebarWidth = 400,
    rightSidebarClassName,
    secondarySidebar,
    forceCollapsed = false,
    bleed = false,
    hideHeader = false,
}: LayoutProps) => {
    const navigation = useNavigation();
    const isNearTop = useNearTop();
    // AIDEV-NOTE: Read sidebar cookie value from root loader for SSR-correct initial render
    const rootData = useRouteLoaderData("root") as
        | { sidebarCollapsed?: boolean }
        | undefined;
    const { isCollapsed, toggleCollapsed, shouldAnimate } = useSidebarCollapsed(
        { forceCollapsed, serverDefault: rootData?.sidebarCollapsed },
    );
    // const { setLoading } = useLoader();

    // // Effect to control the global loader based on prop and navigation state
    // useEffect(() => {
    //     const isLoading = navigation.state === "loading" || loading;
    //     // setLoading(isLoading);
    //     // Optional: Ensure loader is turned off when component unmounts
    //     // return () => setLoading(false);
    // }, [navigation.state, loading, setLoading]);

    return (
        <React.Fragment>
            <GlobalLoading />

            {/* Make the main container transparent or white based on background image visibility */}
            <div
                data-testid={testId}
                className={cn(
                    "items-left flex min-h-screen",
                    // AIDEV-NOTE: Opt-in viewport bound keeps long workspace timelines from displacing docked controls.
                    desktopFullHeight &&
                        "h-dvh min-h-0 overflow-hidden print:h-auto print:overflow-visible",
                )}
                style={{ backgroundColor: "transparent" }}
            >
                {/* AIDEV-NOTE: ScrollRestoration removed - already in root.tsx */}
                {/* AIDEV-NOTE: print:hidden wrapper ensures sidebar is hidden when printing */}
                {!hideSidebar && (
                    <div className="print:hidden">
                        <SidebarNav
                            collapsed={isCollapsed}
                            onToggleCollapsed={toggleCollapsed}
                            shouldAnimate={shouldAnimate}
                            hideCollapseButton={forceCollapsed}
                        />
                    </div>
                )}
                {/* AIDEV-NOTE: Secondary sidebar renders between main sidebar and content.
                    Used for nested navigation (e.g., admin tools). Main sidebar should
                    typically be forced to collapsed state when secondary sidebar is shown. */}
                {secondarySidebar}
                {/* AIDEV-NOTE: min-w-0 allows flex item to shrink below content's intrinsic width when right sidebar is present */}
                <div
                    className="items-left text-brand-forest-800 flex min-h-0 min-w-0 max-w-none flex-1 flex-col overflow-hidden print:overflow-visible"
                    style={{ backgroundColor: "transparent" }}
                    id="layout-body"
                >
                    <ServiceDegradationBanner />
                    <BillingGlobalBanner />
                    <BaaNudgeBanner />
                    <DpaAcceptBanner />
                    {headerBanner && (
                        <div
                            className="mb-0 w-full"
                            style={{ marginBottom: 0 }}
                        >
                            {headerBanner}
                        </div>
                    )}
                    {headerImageUrl && (
                        <div className="h-[250px] w-full">
                            <img
                                src={headerImageUrl}
                                alt="Header background"
                                className="size-full rounded-md object-cover"
                            />
                        </div>
                    )}
                    {/* AIDEV-NOTE: overflow-visible allows EditableContent's save toolbar to extend below header */}
                    {!hideHeader && (
                        <header
                            className={cn(
                                "flex items-center pb-4 border-b border-border md:border-none lg:pt-0 2xl:pt-16 px-12 md:px-8 2xl:px-20 overflow-visible",
                                title && "lg:pt-6 2xl:pt-16",
                                headerBanner && "lg:mt-0 md:mt-4",
                                headerImageUrl && "lg:pt-0",
                                stickyHeader &&
                                    "sticky top-0 z-30 transition-colors duration-200 data-[scrolled=true]:bg-brand-cream-50/80",
                                hideSidebar && "justify-center",
                                desktopFullHeight && "shrink-0",
                            )}
                            style={{ backgroundColor: "#FDFCFA" }}
                            data-scrolled={!isNearTop}
                        >
                            {(title || searchBar || actions) && (
                                <div
                                    className={clsx(
                                        fullWidth
                                            ? "w-full h-full mt-0 pt-0"
                                            : "w-full",
                                        "flex w-full items-start min-h-12 justify-start gap-4",
                                        hideSidebar && "max-w-[600px]",
                                    )}
                                >
                                    {/* AIDEV-NOTE: overflow-visible is required for EditableContent's
                                    absolutely positioned save toolbar to appear below the title.
                                    min-w-0 allows flex shrinking. */}
                                    {title && (
                                        <H1
                                            className={cn(
                                                "mb-0 min-w-0 overflow-visible font-display",
                                            )}
                                        >
                                            {title}
                                        </H1>
                                    )}
                                    {searchBar && (
                                        <div className="flex-1">
                                            {searchBar}
                                        </div>
                                    )}
                                    {/* AIDEV-NOTE: Auto-spacer when no searchBar but actions exist - pushes actions to the right */}
                                    {!searchBar && actions && (
                                        <div className="flex-1" />
                                    )}
                                    {actions && (
                                        <div className="shrink-0">
                                            {actions}
                                        </div>
                                    )}
                                </div>
                            )}
                        </header>
                    )}
                    <main
                        className={cn(
                            "flex-1",
                            fullWidth
                                ? cn(
                                      "flex w-full h-full mt-0",
                                      !bleed && "md:px-8 2xl:px-20",
                                  )
                                : cn(
                                      "pb-6 md:pb-8 w-full",
                                      !bleed && "px-12 md:px-8 2xl:px-20",
                                  ),
                            // AIDEV-NOTE: Add reduced side padding when right sidebar is present
                            desktopFullHeight &&
                                "h-auto min-h-0 overflow-hidden print:overflow-visible",
                        )}
                        style={{
                            backgroundColor: "#FDFCFA",
                        }}
                    >
                        {/* Content container with max width except for single session page or full width pages */}
                        {/* AIDEV-NOTE: flex-1 when fullWidth ensures children can use h-full for sidebars */}
                        {/* AIDEV-NOTE: @container enables container queries for responsive components.
                            Children can use @md:, @lg:, @4xl:, etc. to respond to this container's width
                            instead of viewport width. This is especially useful when rightSidebar is present
                            as it affects available space without changing viewport width. */}
                        <div
                            className={cn(
                                "text-left @container w-full",
                                !fullWidth
                                    ? "max-w-[600px]"
                                    : "max-w-none flex-1",
                                hideSidebar && "mx-auto",
                                desktopFullHeight && "h-full min-h-0",
                            )}
                        >
                            {children}
                        </div>
                        {/* AIDEV-NOTE: Right sidebar - sticky with independent scrolling */}
                    </main>
                </div>
                {rightSidebar && (
                    <aside
                        className={clsx(
                            "sticky top-0 h-screen shrink-0 overflow-y-auto border-l border-border print:hidden",
                            rightSidebarClassName ?? "bg-gray-50",
                        )}
                        style={{ width: rightSidebarWidth }}
                    >
                        {rightSidebar}
                    </aside>
                )}
            </div>
            {/* AIDEV-NOTE: Offset the fixed checklist widget by the right sidebar
                width so it doesn't render underneath an open sidebar */}
            <OnboardingChecklistContainer
                rightOffset={rightSidebar ? rightSidebarWidth : undefined}
            />
        </React.Fragment>
    );
};

export default DesktopLayout;
